import {
  $getSelection,
  $isRangeSelection,
  CLICK_COMMAND,
  $getNearestNodeFromDOMNode
} from "lexical";
import { $isLinkNode } from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Open16Filled } from "@fluentui/react-icons";
import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

export function FloatingLinkUIPlugin() {
  const [editor] = useLexicalComposerContext();

  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const tooltipRef = useRef<HTMLDivElement>(null);
  const openedByClickRef = useRef(false);

  /**
   * Detect LINK CLICKS only
   */
  useEffect(() => {
    return editor.registerCommand(
      CLICK_COMMAND,
      (event: MouseEvent) => {
        const target = event.target as HTMLElement | null;
        if (!target) {
          return false;
        }

        const anchor = target.closest("a");
        if (!anchor) {
          openedByClickRef.current = false;
          setShowTooltip(false);
          return false;
        }

        event.preventDefault();

        editor.update(() => {
          const linkNode = $getNearestNodeFromDOMNode(anchor);
          if ($isLinkNode(linkNode)) {
            linkNode.select();
          }
        });

        openedByClickRef.current = true;
        setShowTooltip(true);

        return true;
      },
      0
    );
  }, [editor]);

  /**
   * Track selection & extract URL
   * Ignore selection noise if tooltip was opened by click
   */
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) {
          if (!openedByClickRef.current) {
            setLinkUrl(null);
            setShowTooltip(false);
          }
          return;
        }

        const node = selection.anchor.getNode();
        const parent = node.getParent();

        if ($isLinkNode(node)) {
          setLinkUrl(node.getURL());
        } else if ($isLinkNode(parent)) {
          setLinkUrl(parent.getURL());
        } else if (!openedByClickRef.current) {
          setLinkUrl(null);
          setShowTooltip(false);
        }
      });
    });
  }, [editor]);

  /**
   * Position tooltip near selection
   */
  useEffect(() => {
    if (!linkUrl || !showTooltip || !tooltipRef.current) {
      return;
    }

    const domSelection = window.getSelection();
    if (!domSelection || domSelection.rangeCount === 0) {
      return;
    }

    const rect = domSelection.getRangeAt(0).getBoundingClientRect();

    tooltipRef.current.style.top = `${rect.bottom + window.scrollY + 4}px`;
    tooltipRef.current.style.left = `${rect.left + window.scrollX}px`;
  }, [linkUrl, showTooltip]);

  /**
   * Close tooltip when clicking outside
   */
  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      const target = e.target as HTMLElement;

      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(target) &&
        !target.closest(".editor-input a")
      ) {
        openedByClickRef.current = false;
        setShowTooltip(false);
      }
    }

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  if (!linkUrl || !showTooltip) {
    return null;
  }

  return ReactDOM.createPortal(
    <div ref={tooltipRef} className="floating-link-tooltip">
      <button
        type="button"
        className="floating-link-action"
        onClick={() => window.open(linkUrl, "_blank", "noopener,noreferrer")}
      >
        Öppna länk
        <Open16Filled
          className="ml-2"
          style={{ width: "14px", height: "14px" }}
        />
      </button>

      <div className="floating-link-url" title={linkUrl}>
        {linkUrl}
      </div>
    </div>,
    document.body
  );
}
